import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { ExitingPersistence, FadeIn } from '@atlaskit/motion';
import { Box, Inline, Pressable, Stack, Text, xcss } from '@atlaskit/primitives';
import React, { type ComponentType, type FunctionComponent, useState } from 'react';

import { FieldTypeMenuItem } from './AddFieldMenuItem.tsx';
import type { AddFieldMenuSelection, FieldTypeEntry } from './types.ts';

const advancedRowStyles = xcss({
  width: 'calc(100% + 8px)',
  marginInlineStart: 'space.negative.050',
  borderRadius: 'radius.small',
  paddingInlineStart: 'space.100',
  paddingInlineEnd: 'space.100',
  paddingBlock: 'space.075',
  color: 'color.text',
  backgroundColor: 'color.background.neutral.subtle',
  ':hover': {
    backgroundColor: 'color.background.neutral.subtle.hovered'
  }
});

const advancedItemsStyles = xcss({
  paddingBlockStart: 'space.025'
});

const itemGridStyles = xcss({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  columnGap: 'space.100',
  rowGap: 'space.025'
});

const productIconWrapperStyles = xcss({
  display: 'inline-flex',
  alignItems: 'center'
});

export const AdvancedFieldTypeSection: FunctionComponent<{
  label: string;
  Icon: ComponentType<{
    appearance?: 'brand';
    label: string;
    size?: 'xxsmall';
    shouldUseNewLogoDesign?: boolean;
  }>;
  items: FieldTypeEntry[];
  isExpandedByDefault?: boolean;
  isExpandedBySearch: boolean;
  onSelect?: (selection: AddFieldMenuSelection) => void;
}> = ({
  label,
  Icon,
  items,
  isExpandedByDefault = false,
  isExpandedBySearch,
  onSelect
}) => {
  const [isExpanded, setIsExpanded] = useState(isExpandedByDefault);
  const shouldShowItems = isExpanded || isExpandedBySearch;

  return (
    <Stack space="space.025">
      <Pressable
        xcss={advancedRowStyles}
        aria-expanded={shouldShowItems}
        onClick={() => setIsExpanded((value) => !value)}
        testId={`add-field-menu-${label.toLocaleLowerCase().replaceAll(' ', '-')}`}
      >
        <Inline spread="space-between" alignBlock="center">
          <Inline space="space.100" alignBlock="center" shouldWrap={false}>
            <Box xcss={productIconWrapperStyles}>
              <Icon appearance="brand" size="xxsmall" label="" shouldUseNewLogoDesign />
            </Box>
            <Text>{label}</Text>
          </Inline>
          {shouldShowItems ? (
            <ChevronDownIcon label="" size="small" />
          ) : (
            <ChevronRightIcon label="" size="small" />
          )}
        </Inline>
      </Pressable>
      <ExitingPersistence>
        {shouldShowItems && (
          <FadeIn duration="small">
            {(motionProps) => (
              <div {...motionProps}>
                <Box xcss={advancedItemsStyles}>
                  <Box xcss={itemGridStyles}>
                    {items.map((fieldType) => (
                      <FieldTypeMenuItem
                        key={fieldType.type}
                        fieldType={fieldType}
                        onSelect={onSelect}
                      />
                    ))}
                  </Box>
                </Box>
              </div>
            )}
          </FadeIn>
        )}
      </ExitingPersistence>
    </Stack>
  );
};
